import React, { useState } from 'react';
import { ConsolidationPanel } from './ConsolidationPanel';
import { api } from '../../services/api';

/** Admin toggle to flip an order between Will Call and Shipping (freight). */
function ShipMethodToggle({ orderId, isWillCall, disabled, onChange }: {
  orderId: string; isWillCall: boolean; disabled?: boolean; onChange?: () => void;
}) {
  const [busy, setBusy] = useState(false);
  const flip = async () => {
    const target = isWillCall ? 'shipping' : 'will_call';
    const label = isWillCall ? 'Shipping (freight)' : 'Will Call';
    const extra = isWillCall ? '' : '\n\nThis clears any selected carrier and freight charges on the order.';
    if (!window.confirm(`Change this order to ${label}?${extra}`)) return;
    setBusy(true);
    try {
      await api.orders.setShippingMethod(orderId, target as 'shipping' | 'will_call');
      onChange?.();
    } catch (e: any) {
      alert(e?.message || 'Failed to change shipping method');
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="flex items-center justify-between bg-white dark:bg-gray-800 border dark:border-gray-700 rounded-lg px-4 py-3">
      <div className="text-sm">
        <span className="text-gray-500 dark:text-gray-400">Method: </span>
        <strong className="text-gray-800 dark:text-white">{isWillCall ? 'Will Call' : 'Shipping (Freight)'}</strong>
      </div>
      <button
        onClick={flip}
        disabled={busy || disabled}
        title={disabled ? 'Remove from consolidated shipment / unbook freight first' : ''}
        className="px-3 py-1.5 text-xs font-medium rounded-lg border border-blue-300 text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 disabled:opacity-40 disabled:cursor-not-allowed"
      >
        {busy ? '…' : isWillCall ? 'Switch to Shipping' : 'Switch to Will Call'}
      </button>
    </div>
  );
}

interface ShippingTabProps {
  order: {
    id: string;
    dealerId?: string;
    dealer_id?: string;
    shipmentId?: string | null;
    shipment_id?: string | null;
    shipping_method?: string;
    shippingMethod?: string;
    shipping_address?: any;
    shipToAddress?: any;
    shippingAddressSource?: string;
    shipping_address_source?: string;
    dealerAddress?: any;
    selected_carrier_name?: string;
    selectedCarrierName?: string;
    selected_carrier_code?: string;
    selectedCarrierCode?: string;
    freight_base_amount?: number;
    freightBaseAmount?: number;
    freight_markup_percent?: number;
    freightMarkupPercent?: number;
    freight_markup_amount?: number;
    freightMarkupAmount?: number;
    freight_total_amount?: number;
    freightTotalAmount?: number;
    freight_quotes?: any[];
    freightQuotes?: any[];
    freightview_shipment_id?: string;
    freightviewShipmentId?: string;
    freight_booked_at?: string;
    freightBookedAt?: string;
    freight_bol_number?: string;
    freightBolNumber?: string;
    freight_pro_number?: string;
    freightProNumber?: string;
    freight_pickup_confirmation?: string;
    freightPickupConfirmation?: string;
    selected_quote_id?: string;
    selectedQuoteId?: string;
    // New tracking fields
    tracking_number?: string;
    trackingNumber?: string;
    freight_tracking_status?: string;
    freight_delivery_estimate?: string;
    freight_tracking_events?: any;
    freight_documents?: any;
    freight_tracking_synced_at?: string;
    freight_shipment_details_url?: string;
    freight_canceled_at?: string;
    // Parcel (small-package) fields — separate from LTL freight
    parcel_tracking_number?: string;
    parcelTrackingNumber?: string;
    parcel_carrier?: string;
    parcelCarrier?: string;
    parcel_service_name?: string;
    parcelServiceName?: string;
    shipping_carrier_type?: string;
    shippingCarrierType?: string;
    shipped_at?: string;
    shippedAt?: string;
  };
  onChange?: () => void;
}

// Icons
const TruckIcon = () => (
  <svg className="w-10 h-10 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8.25 18.75a1.5 1.5 0 0 1-3 0m3 0a1.5 1.5 0 0 0-3 0m3 0h6m-9 0H3.375a1.125 1.125 0 0 1-1.125-1.125V14.25m17.25 4.5a1.5 1.5 0 0 1-3 0m3 0a1.5 1.5 0 0 0-3 0m3 0h1.125c.621 0 1.129-.504 1.09-1.124a17.902 17.902 0 0 0-3.213-9.193 2.056 2.056 0 0 0-1.58-.86H14.25M16.5 18.75h-2.25m0-11.177v-.958c0-.568-.422-1.048-.987-1.106a48.554 48.554 0 0 0-10.026 0 1.106 1.106 0 0 0-.987 1.106v7.635m12-6.677v6.677m0 4.5v-4.5m0 0h-12" />
  </svg>
);

const BuildingIcon = () => (
  <svg className="w-10 h-10 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3.75 21h16.5M4.5 3h15M5.25 3v18m13.5-18v18M9 6.75h1.5m-1.5 3h1.5m-1.5 3h1.5m3-6H15m-1.5 3H15m-1.5 3H15M9 21v-3.375c0-.621.504-1.125 1.125-1.125h3.75c.621 0 1.125.504 1.125 1.125V21" />
  </svg>
);

const CheckCircleIcon = ({ className = "w-5 h-5" }: { className?: string }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
  </svg>
);

const ClockIcon = () => (
  <svg className="w-5 h-5 text-yellow-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z" />
  </svg>
);

function parseJsonField(val: any): any[] {
  if (!val) return [];
  if (Array.isArray(val)) return val;
  if (typeof val === 'string') {
    try { return JSON.parse(val); } catch { return []; }
  }
  return [];
}

function getTrackingStatusBadge(status?: string) {
  if (!status) return null;
  const map: Record<string, { bg: string; text: string; label: string }> = {
    booked: { bg: 'bg-blue-100 dark:bg-blue-900/30', text: 'text-blue-700 dark:text-blue-300', label: 'Booked' },
    confirmed: { bg: 'bg-blue-100 dark:bg-blue-900/30', text: 'text-blue-700 dark:text-blue-300', label: 'Confirmed' },
    in_transit: { bg: 'bg-indigo-100 dark:bg-indigo-900/30', text: 'text-indigo-700 dark:text-indigo-300', label: 'In Transit' },
    delivered: { bg: 'bg-green-100 dark:bg-green-900/30', text: 'text-green-700 dark:text-green-300', label: 'Delivered' },
    canceled: { bg: 'bg-red-100 dark:bg-red-900/30', text: 'text-red-700 dark:text-red-300', label: 'Canceled' },
  };
  const s = map[status] || { bg: 'bg-gray-100 dark:bg-gray-700', text: 'text-gray-700 dark:text-gray-300', label: status };
  return (
    <span className={`px-2.5 py-1 text-xs font-medium rounded-full ${s.bg} ${s.text}`}>
      {s.label}
    </span>
  );
}

export function ShippingTab({ order, onChange }: ShippingTabProps) {
  const [voiding, setVoiding] = useState(false);
  const shippingMethod = order.shippingMethod || order.shipping_method;
  const dealerId = order.dealerId || order.dealer_id;
  const consolidatedShipmentId = order.shipmentId ?? order.shipment_id ?? null;

  // Resolve camelCase (from API) with snake_case fallback
  const carrierName = order.selectedCarrierName || order.selected_carrier_name;
  const carrierCode = order.selectedCarrierCode || order.selected_carrier_code;
  const baseAmount = order.freightBaseAmount ?? order.freight_base_amount;
  const markupPercent = order.freightMarkupPercent ?? order.freight_markup_percent;
  const markupAmount = order.freightMarkupAmount ?? order.freight_markup_amount;
  const totalAmount = order.freightTotalAmount ?? order.freight_total_amount;
  // Actual carrier cost = recorded freight_cost (set via "Set Cost") if present,
  // else the quoted base. Real profit is what the dealer paid minus that cost —
  // NOT the theoretical markup (they can diverge, e.g. carrier billed more than
  // the dealer was charged → a loss).
  const carrierCost = (order as any).freightCost ?? (order as any).freight_cost ?? baseAmount;
  const freightProfit = Number(totalAmount || 0) - Number(carrierCost || 0);
  // Original quote preserved before any actual-freight adjustment
  const origFreightTotal = (order as any).origFreightTotal ?? (order as any).orig_freight_total ?? null;
  const origFreightCarrier = (order as any).origFreightCarrier ?? (order as any).orig_freight_carrier ?? null;
  const origFreightBase = (order as any).origFreightBase ?? (order as any).orig_freight_base ?? null;
  const freightChangedFromQuote = origFreightTotal != null && Math.abs(Number(origFreightTotal) - Number(totalAmount || 0)) >= 0.01;
  const quotes = order.freightQuotes || order.freight_quotes;
  const shipmentId = order.freightviewShipmentId || order.freightview_shipment_id;
  const bookedAt = order.freightBookedAt || order.freight_booked_at;
  const bolNumber = order.freightBolNumber || order.freight_bol_number;
  const proNumber = order.freightProNumber || order.freight_pro_number;
  const freightTrackingUrl = (order as any).freightTrackingUrl || (order as any).freight_tracking_url || null;
  const pickupConfirmation = order.freightPickupConfirmation || order.freight_pickup_confirmation;
  const quoteId = order.selectedQuoteId || order.selected_quote_id;
  const trackingNumber = order.trackingNumber || order.tracking_number;

  // Manual carrier / BOL / PRO entry. These could only be set on the shipping
  // portal's Ready to Ship screen, which an order has already moved past once
  // it is shipped — so anything marked shipped by a direct status change had
  // nowhere to record a PRO. 34 of 47 shipped freight orders have no tracking.
  const [trkOpen, setTrkOpen] = useState(false);
  const [trkCarrier, setTrkCarrier] = useState('');
  const [trkBol, setTrkBol] = useState('');
  const [trkPro, setTrkPro] = useState('');
  const [trkSaving, setTrkSaving] = useState(false);
  const [trkError, setTrkError] = useState('');

  const openTracking = () => {
    setTrkCarrier(carrierName || '');
    setTrkBol(bolNumber || '');
    setTrkPro(proNumber || trackingNumber || '');
    setTrkError('');
    setTrkOpen(true);
  };

  const saveTracking = async () => {
    setTrkSaving(true);
    setTrkError('');
    try {
      await api.orders.updateShippingTracking(order.id, {
        carrier: trkCarrier.trim() || undefined,
        bolNumber: trkBol.trim() || undefined,
        proNumber: trkPro.trim() || undefined,
      });
      setTrkOpen(false);
      onChange?.();
    } catch (e: any) {
      setTrkError(e?.message || 'Failed to save tracking');
    } finally {
      setTrkSaving(false);
    }
  };
  const trackingStatus = order.freight_tracking_status;
  const deliveryEstimate = order.freight_delivery_estimate;
  const trackingEvents = parseJsonField(order.freight_tracking_events);

  // Pull latest FreightView tracking/PRO/BOL/status — same sync the shipping
  // portal runs, surfaced here so it doesn't require leaving the factory app.
  const [syncing, setSyncing] = useState(false);
  const handleSyncTracking = async () => {
    setSyncing(true);
    try {
      await api.orders.syncFreightTracking(order.id);
      onChange?.();
    } catch (e: any) {
      alert(e?.message || 'Failed to sync tracking from FreightView');
    } finally {
      setSyncing(false);
    }
  };
  const SyncButton = () => (
    <button
      onClick={handleSyncTracking}
      disabled={syncing}
      title="Pull latest tracking, PRO, BOL and status from FreightView"
      className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-lg bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50"
    >
      <svg className={`w-4 h-4 ${syncing ? 'animate-spin' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" /></svg>
      {syncing ? 'Syncing…' : 'Sync from FreightView'}
    </button>
  );
  const documents = parseJsonField(order.freight_documents);
  // Parcel (UPS/FedEx/USPS) — separate from LTL freight. Many orders ship
  // small package and have NO LTL carrier selected at all, so the freight
  // "Booked" block never renders. Surface parcel tracking independently so
  // factory admin gets the same clickable tracking link as the dealer portal.
  const parcelTracking = order.parcelTrackingNumber || order.parcel_tracking_number;
  const parcelCarrier = order.parcelCarrier || order.parcel_carrier;
  const parcelService = order.parcelServiceName || order.parcel_service_name;
  const shippedAt = order.shippedAt || order.shipped_at;
  const parcelTrackingUrl = (() => {
    if (!parcelTracking) return null;
    const c = (parcelCarrier || 'ups').toLowerCase();
    if (c === 'ups') return `https://www.ups.com/track?tracknum=${parcelTracking}`;
    if (c === 'fedex') return `https://www.fedex.com/fedextrack/?trknbr=${parcelTracking}`;
    if (c === 'usps') return `https://tools.usps.com/go/TrackConfirmAction?tLabels=${parcelTracking}`;
    return null;
  })();
  const syncedAt = order.freight_tracking_synced_at;
  const detailsUrl = order.freight_shipment_details_url;

  // Will Call - Simple display (still allow consolidation: will-call orders
  // get auto-converted to freight when added to a shipment)
  if (shippingMethod === 'will_call' || shippingMethod === 'willcall') {
    return (
      <div className="space-y-6">
        <ShipMethodToggle orderId={order.id} isWillCall disabled={!!consolidatedShipmentId} onChange={onChange} />
        {dealerId && (
          <ConsolidationPanel
            orderId={order.id}
            dealerId={dealerId}
            shipmentId={consolidatedShipmentId}
            onChange={onChange}
          />
        )}
        <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-6">
          <div className="flex items-center gap-4">
            <BuildingIcon />
            <div>
              <h3 className="text-lg font-semibold text-gray-800 dark:text-white">Will Call</h3>
              <p className="text-gray-600 dark:text-gray-300">Customer will pickup from factory</p>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                12446 State Hwy 205, Lavon, TX 75166
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Shipping - Full display
  const shippingAddress = order.shipToAddress || order.shipping_address;
  const parsedAddress = typeof shippingAddress === 'string'
    ? JSON.parse(shippingAddress)
    : shippingAddress;

  const freightBooked = !!(order.freightBookedAt || order.freight_booked_at);
  return (
    <div className="space-y-6">
      <ShipMethodToggle orderId={order.id} isWillCall={false} disabled={!!consolidatedShipmentId || freightBooked} onChange={onChange} />
      {/* Consolidation panel. Renders nothing if no eligible siblings AND not
          already in a shipment. */}
      {dealerId && (
        <ConsolidationPanel
          orderId={order.id}
          dealerId={dealerId}
          shipmentId={consolidatedShipmentId}
          onChange={onChange}
        />
      )}

      {/* Destination */}
      {parsedAddress && (
        <div className="bg-white dark:bg-gray-800 border dark:border-gray-700 rounded-lg p-4">
          <div className="flex items-center justify-between mb-2">
            <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400 uppercase">Ship To</h4>
            {(() => {
              // What the dealer actually selected on the order form. Only the
              // resulting address used to reach the factory, so a drop-ship to a
              // homeowner looked identical to a delivery to the dealer's own
              // warehouse. Newer orders carry the choice; older ones are inferred
              // by matching the ship-to against the dealer's address on file.
              const src = (order.shippingAddressSource || order.shipping_address_source || '').toLowerCase();
              const dealerAddr = (order as any).dealerAddress || {};
              const sameAsDealer = !!(dealerAddr.street && parsedAddress?.street
                && String(dealerAddr.street).trim().toLowerCase() === String(parsedAddress.street).trim().toLowerCase());
              const resolved = src || (sameAsDealer ? 'dealer' : 'customer');
              const label = resolved === 'dealer' ? "Dealer's business address"
                : resolved === 'willcall' ? 'Will Call pickup'
                : "Customer / job-site address";
              const tone = resolved === 'dealer'
                ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                : 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400';
              return (
                <span className={`px-2 py-0.5 rounded text-[11px] font-semibold ${tone}`}
                      title={src ? 'Selected by the dealer on the order form'
                                 : 'Inferred — this order predates the shipping selection being recorded'}>
                  {label}{!src && ' (inferred)'}
                </span>
              );
            })()}
          </div>
          <p className="text-gray-800 dark:text-gray-200">
            {parsedAddress?.company && <strong>{parsedAddress.company}<br /></strong>}
            {parsedAddress?.name && <>{parsedAddress.name}<br /></>}
            {parsedAddress?.contact || parsedAddress?.contactName}{parsedAddress?.contact || parsedAddress?.contactName ? <br /> : null}
            {parsedAddress?.address || parsedAddress?.street}<br />
            {parsedAddress?.city}, {parsedAddress?.state} {parsedAddress?.zip || parsedAddress?.postalCode}
          </p>
          {parsedAddress?.phone && (
            <p className="text-gray-600 dark:text-gray-400 mt-1">{parsedAddress.phone}</p>
          )}
        </div>
      )}

      {/* Manual tracking entry — available at ANY status */}
      <div className="bg-white dark:bg-gray-800 border dark:border-gray-700 rounded-lg p-4">
        <div className="flex items-center justify-between">
          <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400 uppercase">Tracking</h4>
          {!trkOpen && (
            <button onClick={openTracking}
              className="text-xs px-2 py-1 rounded bg-blue-600 text-white hover:bg-blue-700">
              {proNumber || bolNumber || carrierName ? 'Edit' : 'Enter tracking'}
            </button>
          )}
        </div>

        {!trkOpen ? (
          <div className="mt-2 text-sm text-gray-800 dark:text-gray-200 space-y-0.5">
            <div>Carrier: <strong>{carrierName || <span className="text-amber-600">not recorded</span>}</strong></div>
            <div>BOL: <strong>{bolNumber || <span className="text-amber-600">not recorded</span>}</strong></div>
            <div>PRO: <strong>{proNumber || trackingNumber || <span className="text-amber-600">not recorded</span>}</strong></div>
          </div>
        ) : (
          <div className="mt-3 space-y-2">
            <div>
              <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">Carrier</label>
              <input value={trkCarrier} onChange={e => setTrkCarrier(e.target.value)}
                placeholder="e.g. C.H. Robinson"
                className="w-full px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded dark:bg-gray-700 dark:text-gray-100" />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">BOL #</label>
                <input value={trkBol} onChange={e => setTrkBol(e.target.value)}
                  placeholder="Bill of Lading"
                  className="w-full px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded dark:bg-gray-700 dark:text-gray-100" />
              </div>
              <div>
                <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">PRO / Tracking #</label>
                <input value={trkPro} onChange={e => setTrkPro(e.target.value)}
                  placeholder="Carrier PRO"
                  className="w-full px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded dark:bg-gray-700 dark:text-gray-100" />
              </div>
            </div>
            {trkError && <p className="text-xs text-red-600">{trkError}</p>}
            <p className="text-[11px] text-gray-500 dark:text-gray-400">
              Saves the PRO as the dealer's tracking link. Sets the ship date if the order is
              already shipped and never got one.
            </p>
            <div className="flex gap-2">
              <button onClick={saveTracking} disabled={trkSaving}
                className="text-xs px-3 py-1.5 rounded bg-green-600 text-white hover:bg-green-700 disabled:opacity-50">
                {trkSaving ? 'Saving…' : 'Save'}
              </button>
              <button onClick={() => setTrkOpen(false)} disabled={trkSaving}
                className="text-xs px-3 py-1.5 rounded bg-gray-200 dark:bg-gray-700 dark:text-gray-200 hover:bg-gray-300">
                Cancel
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Selected Carrier - Highlighted */}
      {carrierName ? (
        <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4">
          <div className="flex justify-between items-start mb-4">
            <div>
              <span className="text-xs font-medium text-green-600 dark:text-green-400 uppercase tracking-wide">
                Dealer Selected Carrier
              </span>
              <h3 className="text-xl font-bold text-gray-800 dark:text-white mt-1">
                {carrierName}
              </h3>
            </div>
            <div className="text-right">
              <span className="text-xs text-gray-500 dark:text-gray-400">Dealer Paid</span>
              <p className="text-2xl font-bold text-green-600 dark:text-green-400">
                ${Number(totalAmount || 0).toFixed(2)}
              </p>
            </div>
          </div>

          {/* Original quote — preserved so the change from the quoted freight is visible */}
          {freightChangedFromQuote && (
            <div className="border-t border-green-200 dark:border-green-800 pt-4 mt-4">
              <h5 className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase mb-3">Original Quote (before adjustment)</h5>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                <div className="bg-white dark:bg-gray-800 rounded p-3">
                  <span className="text-xs text-gray-500 dark:text-gray-400">Quoted Carrier</span>
                  <p className="text-sm font-medium text-gray-800 dark:text-white">{origFreightCarrier || '—'}</p>
                </div>
                {origFreightBase != null && (
                  <div className="bg-white dark:bg-gray-800 rounded p-3">
                    <span className="text-xs text-gray-500 dark:text-gray-400">Quoted Base</span>
                    <p className="text-lg font-semibold text-gray-800 dark:text-white">${Number(origFreightBase).toFixed(2)}</p>
                  </div>
                )}
                <div className="bg-white dark:bg-gray-800 rounded p-3">
                  <span className="text-xs text-gray-500 dark:text-gray-400">Quoted Total</span>
                  <p className="text-lg font-semibold text-gray-800 dark:text-white">${Number(origFreightTotal).toFixed(2)}</p>
                </div>
                <div className="bg-white dark:bg-gray-800 rounded p-3">
                  <span className="text-xs text-gray-500 dark:text-gray-400">Change vs Quote</span>
                  {(() => { const d = Number(totalAmount || 0) - Number(origFreightTotal); return (
                    <p className={`text-lg font-semibold ${d > 0 ? 'text-red-600 dark:text-red-400' : 'text-green-600 dark:text-green-400'}`}>{d > 0 ? '+' : ''}${d.toFixed(2)}</p>
                  ); })()}
                </div>
              </div>
            </div>
          )}

          {/* Internal Pricing Breakdown - Only visible to factory staff */}
          <div className="border-t border-green-200 dark:border-green-800 pt-4 mt-4">
            <h5 className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase mb-3">
              Internal Pricing (Not visible to dealers)
            </h5>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              <div className="bg-white dark:bg-gray-800 rounded p-3">
                <span className="text-xs text-gray-500 dark:text-gray-400">Carrier Cost</span>
                <p className="text-lg font-semibold text-gray-800 dark:text-white">
                  ${Number(carrierCost || 0).toFixed(2)}
                </p>
              </div>
              <div className="bg-white dark:bg-gray-800 rounded p-3">
                <span className="text-xs text-gray-500 dark:text-gray-400">Markup %</span>
                <p className="text-lg font-semibold text-gray-800 dark:text-white">
                  {markupPercent || 0}%
                </p>
              </div>
              <div className="bg-white dark:bg-gray-800 rounded p-3">
                <span className="text-xs text-gray-500 dark:text-gray-400">Markup $</span>
                <p className="text-lg font-semibold text-gray-800 dark:text-white">
                  ${Number(markupAmount || 0).toFixed(2)}
                </p>
              </div>
              <div className="bg-white dark:bg-gray-800 rounded p-3">
                <span className="text-xs text-gray-500 dark:text-gray-400">{freightProfit < 0 ? 'Loss' : 'Profit'}</span>
                <p className={`text-lg font-semibold ${freightProfit < 0 ? 'text-red-600 dark:text-red-400' : 'text-green-600 dark:text-green-400'}`}>
                  {freightProfit < 0 ? '-' : ''}${Math.abs(freightProfit).toFixed(2)}
                </p>
              </div>
            </div>
          </div>
        </div>
      ) : parcelTracking ? (
        <div className="bg-indigo-50 dark:bg-indigo-900/20 border border-indigo-200 dark:border-indigo-800 rounded-lg p-4">
          <div className="flex items-center gap-3 mb-3">
            <TruckIcon />
            <div>
              <p className="text-indigo-800 dark:text-indigo-300 font-semibold">
                {(parcelCarrier || 'UPS').toUpperCase()}{parcelService ? ` — ${parcelService}` : ''}
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400">Small-package shipment</p>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <span className="text-xs text-gray-500 dark:text-gray-400 uppercase">Tracking Number</span>
              {parcelTrackingUrl ? (
                <a
                  href={parcelTrackingUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-mono text-base text-indigo-600 dark:text-indigo-400 underline hover:text-indigo-800 dark:hover:text-indigo-200 flex items-center gap-1"
                >
                  {parcelTracking}
                  <svg className="w-3.5 h-3.5 inline-block" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                  </svg>
                </a>
              ) : (
                <p className="font-mono text-base text-indigo-700 dark:text-indigo-300">{parcelTracking}</p>
              )}
            </div>
            {shippedAt && (
              <div>
                <span className="text-xs text-gray-500 dark:text-gray-400 uppercase">Shipped</span>
                <p className="text-base text-gray-800 dark:text-white">
                  {new Date(shippedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                </p>
              </div>
            )}
          </div>
          <div className="mt-3 flex items-center gap-3">
            <button
              onClick={async () => {
                if (!confirm('Void this label at the carrier? It can then be re-bought.\n\nHeads up: UPS and FedEx only allow voids the SAME DAY — before the end-of-day close/manifest.')) return;
                setVoiding(true);
                try {
                  const r = await api.orders.voidParcelLabel(order.id);
                  if ((r as any)?.success === false) alert(`Void failed: ${(r as any).error || 'carrier rejected the void'}`);
                  else onChange?.();
                } catch (e: any) {
                  alert(`Void failed: ${e?.message || e}`);
                } finally { setVoiding(false); }
              }}
              disabled={voiding}
              className="px-3 py-1.5 text-sm font-medium rounded-lg border border-red-300 text-red-700 hover:bg-red-50 dark:border-red-800 dark:text-red-300 dark:hover:bg-red-900/30 disabled:opacity-50"
            >
              {voiding ? 'Voiding…' : 'Void Label'}
            </button>
            <span className="text-xs text-gray-400">Same-day only (before carrier end-of-day close)</span>
          </div>
        </div>
      ) : (
        <div className="bg-gray-50 dark:bg-gray-800 border dark:border-gray-700 rounded-lg p-4">
          <div className="flex items-center gap-3">
            <TruckIcon />
            <div>
              <p className="text-gray-600 dark:text-gray-300 font-medium">No carrier selected yet</p>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Dealer has not selected a carrier for this order
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Booking Status */}
      {bookedAt ? (
        <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <CheckCircleIcon className="w-5 h-5 text-blue-600 dark:text-blue-400" />
              <h4 className="font-semibold text-blue-800 dark:text-blue-300">Shipment Booked</h4>
              <span className="text-sm text-blue-600 dark:text-blue-400">
                {new Date(bookedAt).toLocaleDateString()}
              </span>
            </div>
            <div className="flex items-center gap-2">
              {trackingStatus && getTrackingStatusBadge(trackingStatus)}
              <SyncButton />
            </div>
          </div>
          {syncedAt && (
            <div className="text-xs text-gray-400 dark:text-gray-500 mb-3">Last synced {new Date(syncedAt).toLocaleString()}</div>
          )}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div>
              <span className="text-xs text-gray-500 dark:text-gray-400 uppercase">BOL Number</span>
              <p className="font-mono text-lg font-medium text-gray-800 dark:text-white">
                {bolNumber && freightTrackingUrl ? (
                  <a href={freightTrackingUrl} target="_blank" rel="noopener noreferrer" className="text-blue-600 dark:text-blue-400 hover:underline">{bolNumber} ↗</a>
                ) : (bolNumber || '—')}
              </p>
            </div>
            <div>
              <span className="text-xs text-gray-500 dark:text-gray-400 uppercase">PRO Number</span>
              <p className="font-mono text-lg font-medium text-gray-800 dark:text-white">
                {proNumber && freightTrackingUrl ? (
                  <a href={freightTrackingUrl} target="_blank" rel="noopener noreferrer" className="text-blue-600 dark:text-blue-400 hover:underline">{proNumber} ↗</a>
                ) : (proNumber || '—')}
              </p>
            </div>
            <div>
              <span className="text-xs text-gray-500 dark:text-gray-400 uppercase">Pickup Confirmation</span>
              <p className="font-mono text-lg font-medium text-gray-800 dark:text-white">
                {pickupConfirmation || '—'}
              </p>
            </div>
          </div>
          {/* Tracking Number — clickable when carrier matches UPS/FedEx/USPS */}
          {trackingNumber && (() => {
            const c = (carrierName || '').toLowerCase();
            let trackingUrl: string | null = null;
            if (c.includes('ups')) trackingUrl = `https://www.ups.com/track?tracknum=${trackingNumber}`;
            else if (c.includes('fedex')) trackingUrl = `https://www.fedex.com/fedextrack/?trknbr=${trackingNumber}`;
            else if (c.includes('usps')) trackingUrl = `https://tools.usps.com/go/TrackConfirmAction?tLabels=${trackingNumber}`;
            return (
              <div className="mt-4 pt-4 border-t border-blue-200 dark:border-blue-800">
                <span className="text-xs text-gray-500 dark:text-gray-400 uppercase">Tracking Number</span>
                {trackingUrl ? (
                  <a
                    href={trackingUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="font-mono text-lg font-medium text-indigo-600 dark:text-indigo-400 underline hover:text-indigo-800 dark:hover:text-indigo-200 flex items-center gap-1"
                  >
                    {trackingNumber}
                    <svg className="w-3.5 h-3.5 inline-block" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                    </svg>
                  </a>
                ) : (
                  <p className="font-mono text-lg font-medium text-indigo-600 dark:text-indigo-400">
                    {trackingNumber}
                  </p>
                )}
              </div>
            );
          })()}
          {/* Delivery Estimate */}
          {deliveryEstimate && (
            <div className="mt-3 flex items-center gap-2">
              <svg className="w-4 h-4 text-blue-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
              <span className="text-sm text-blue-700 dark:text-blue-300">
                ETA: {new Date(deliveryEstimate + 'T00:00:00').toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' })}
              </span>
            </div>
          )}
        </div>
      ) : carrierName ? (
        <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4">
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2">
              <ClockIcon />
              <p className="text-yellow-800 dark:text-yellow-300 font-medium">
                {(bolNumber || proNumber || pickupConfirmation) ? 'Booked (manual entry)' : 'Awaiting shipment booking'}
              </p>
            </div>
            {shipmentId && <SyncButton />}
          </div>
          {!(bolNumber || proNumber || pickupConfirmation) && (
            <p className="text-sm text-yellow-700 dark:text-yellow-400 mt-1">
              Shipping Portal will book when order is ready to ship
            </p>
          )}
          {/* Surface BOL / PRO / Pickup if they've been entered manually
              even when freight_booked_at wasn't stamped (covers freight
              orders booked outside Freightview where the BOL/PRO were
              typed in by hand). */}
          {(bolNumber || proNumber || pickupConfirmation) && (
            <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div>
                <span className="text-xs text-gray-500 dark:text-gray-400 uppercase">BOL Number</span>
                <p className="font-mono text-lg font-medium text-gray-800 dark:text-white">
                  {bolNumber || '—'}
                </p>
              </div>
              <div>
                <span className="text-xs text-gray-500 dark:text-gray-400 uppercase">PRO Number</span>
                <p className="font-mono text-lg font-medium text-gray-800 dark:text-white">
                  {proNumber || '—'}
                </p>
              </div>
              <div>
                <span className="text-xs text-gray-500 dark:text-gray-400 uppercase">Pickup Confirmation</span>
                <p className="font-mono text-lg font-medium text-gray-800 dark:text-white">
                  {pickupConfirmation || '—'}
                </p>
              </div>
            </div>
          )}
          {/* Tracking link if hand-entered for a recognized carrier */}
          {trackingNumber && (() => {
            const c = (carrierName || '').toLowerCase();
            let trackingUrl: string | null = null;
            if (c.includes('ups')) trackingUrl = `https://www.ups.com/track?tracknum=${trackingNumber}`;
            else if (c.includes('fedex')) trackingUrl = `https://www.fedex.com/fedextrack/?trknbr=${trackingNumber}`;
            else if (c.includes('usps')) trackingUrl = `https://tools.usps.com/go/TrackConfirmAction?tLabels=${trackingNumber}`;
            return (
              <div className="mt-4 pt-4 border-t border-yellow-200 dark:border-yellow-800">
                <span className="text-xs text-gray-500 dark:text-gray-400 uppercase">Tracking Number</span>
                {trackingUrl ? (
                  <a
                    href={trackingUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="font-mono text-lg font-medium text-indigo-600 dark:text-indigo-400 underline hover:text-indigo-800 dark:hover:text-indigo-200 flex items-center gap-1"
                  >
                    {trackingNumber}
                    <svg className="w-3.5 h-3.5 inline-block" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                    </svg>
                  </a>
                ) : (
                  <p className="font-mono text-lg font-medium text-indigo-600 dark:text-indigo-400">
                    {trackingNumber}
                  </p>
                )}
              </div>
            );
          })()}
        </div>
      ) : null}

      {/* Tracking Timeline */}
      {trackingEvents.length > 0 && (
        <div className="bg-white dark:bg-gray-800 border dark:border-gray-700 rounded-lg p-4">
          <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400 uppercase mb-4">Tracking Timeline</h4>
          <div className="space-y-0">
            {trackingEvents.map((event: any, idx: number) => (
              <div key={idx} className="flex gap-3">
                <div className="flex flex-col items-center">
                  <div className={`w-2.5 h-2.5 rounded-full mt-1.5 ${idx === 0 ? 'bg-blue-500' : 'bg-gray-400 dark:bg-gray-600'}`} />
                  {idx < trackingEvents.length - 1 && (
                    <div className="w-0.5 flex-1 bg-gray-200 dark:bg-gray-700 min-h-[20px]" />
                  )}
                </div>
                <div className="pb-4">
                  <p className="text-sm font-medium text-gray-800 dark:text-white">{event.status || event.description}</p>
                  {event.description && event.status && (
                    <p className="text-xs text-gray-500 dark:text-gray-400">{event.description}</p>
                  )}
                  <div className="flex items-center gap-2 mt-0.5">
                    {event.location && (
                      <span className="text-xs text-gray-500 dark:text-gray-400">{event.location}</span>
                    )}
                    {event.date && (
                      <span className="text-xs text-gray-400 dark:text-gray-500">{event.date}{event.time ? ` ${event.time}` : ''}</span>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Documents */}
      {documents.length > 0 && (
        <div className="bg-white dark:bg-gray-800 border dark:border-gray-700 rounded-lg p-4">
          <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400 uppercase mb-3">Documents</h4>
          <div className="space-y-2">
            {documents.map((doc: any, idx: number) => (
              <div key={idx} className="flex items-center justify-between py-2 px-3 bg-gray-50 dark:bg-gray-900 rounded">
                <div className="flex items-center gap-2">
                  <svg className="w-4 h-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  <span className="text-sm text-gray-700 dark:text-gray-300">{doc.name || doc.type || 'Document'}</span>
                  <span className="text-xs px-1.5 py-0.5 rounded bg-gray-200 dark:bg-gray-700 text-gray-500 dark:text-gray-400">
                    {doc.type}
                  </span>
                </div>
                {doc.url && (
                  <a
                    href={doc.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-xs text-blue-600 dark:text-blue-400 hover:underline"
                  >
                    View
                  </a>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* All Available Quotes (Collapsible) */}
      {quotes && quotes.length > 0 && (
        <details className="bg-gray-50 dark:bg-gray-800 rounded-lg">
          <summary className="p-4 cursor-pointer font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg">
            View All Carrier Quotes ({quotes.length} carriers)
          </summary>
          <div className="p-4 pt-0 space-y-2">
            {[...quotes]
              .sort((a: any, b: any) => (a.totalAmount || 0) - (b.totalAmount || 0))
              .map((quote: any, index: number) => (
                <div
                  key={quote.quoteId || index}
                  className={`flex justify-between items-center p-3 rounded border ${
                    quote.quoteId === quoteId
                      ? 'bg-green-50 dark:bg-green-900/30 border-green-300 dark:border-green-700'
                      : 'bg-white dark:bg-gray-700 border-gray-200 dark:border-gray-600'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    {quote.quoteId === quoteId && (
                      <CheckCircleIcon className="w-5 h-5 text-green-600 dark:text-green-400" />
                    )}
                    <div>
                      <span className="font-medium text-gray-800 dark:text-white">
                        {quote.carrierName}
                      </span>
                      {index === 0 && (
                        <span className="ml-2 text-xs bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300 px-2 py-0.5 rounded">
                          Lowest
                        </span>
                      )}
                      {quote.quoteId === quoteId && (
                        <span className="ml-2 text-xs bg-green-100 dark:bg-green-900 text-green-700 dark:text-green-300 px-2 py-0.5 rounded">
                          Selected
                        </span>
                      )}
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        {quote.transitTime || `${quote.transitDaysMin}-${quote.transitDaysMax} days`}
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="font-medium text-gray-800 dark:text-white">
                      ${quote.totalAmount?.toFixed(2)} <span className="text-xs text-gray-500 dark:text-gray-400">dealer</span>
                    </p>
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      ${quote.baseAmount?.toFixed(2)} <span className="text-xs">carrier</span>
                    </p>
                  </div>
                </div>
              ))}
          </div>
        </details>
      )}

      {/* Freightview Reference + Sync Info */}
      {shipmentId && (
        <div className="flex items-center justify-between text-xs text-gray-400 dark:text-gray-500">
          <span>FreightView ID: {shipmentId}</span>
          <div className="flex items-center gap-3">
            {syncedAt && (
              <span>Synced: {new Date(syncedAt).toLocaleString()}</span>
            )}
            {detailsUrl && (
              <a
                href={detailsUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-500 hover:text-blue-400 flex items-center gap-1"
              >
                Open in FreightView
                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                </svg>
              </a>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default ShippingTab;
